import React, { useCallback, useEffect, useMemo } from 'react';
import EditableTable from '@components/EditableTable';
import { Form, FormInstance, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  DeviceActionProps,
  DeviceActionStepsProps,
} from '@components/Actions/ActionsModal/types';
import ActionTypes from '@utils/deviceActions';
import { SyncOutlined } from '@ant-design/icons';
import { RolesTypes } from '@utils/rolesConstants';
import { IDevice } from '@pages/Assets/Inventory/types';

interface ExchangeStepProps {
  type: 'ICCID' | 'MSISDN';
  role: RolesTypes;
  selectedRole?: RolesTypes;
  currentValue?: string;
  devices: IDevice[];
  isUpload?: boolean;
  form: FormInstance<any>;
}

const useExchangeStep = ({
  type,
  role,
  selectedRole,
  currentValue,
  devices,
  isUpload,
  form,
}: ExchangeStepProps): DeviceActionProps => {
  const { t } = useTranslation('actions-modal');
  const changedDevices = Form.useWatch('changedDevices', form);
  const newItem = Form.useWatch('newItem', form);

  const isOnlyOneDevice = useMemo(() => devices?.length === 1, [devices]);

  const treatedDevices = useMemo(
    () =>
      devices?.map(device => ({
        key: device?.id,
        msisdn: device?.msisdn,
        iccid: device?.iccid,
      })),
    [devices],
  );

  const handleDevicesChange = useCallback(
    (items: { [key: string]: any }[]) => {
      form?.setFieldValue('changedDevices', items);
    },
    [form],
  );

  useEffect(() => {
    if (selectedRole === role && !changedDevices) {
      handleDevicesChange(treatedDevices);
    }
  }, [handleDevicesChange, treatedDevices, selectedRole, role, changedDevices]);

  const columns = useMemo(
    () => [
      {
        title: type,
        key: type?.toLowerCase(),
        dataIndex: type?.toLowerCase(),
      },
      {
        title: t('exchange', { type }),
        key: 'processData',
        dataIndex: 'processData',
        editable: true,
      },
    ],
    [t, type],
  );

  const steps: DeviceActionStepsProps[] = useMemo(
    () => [
      {
        title: t('exchange', { type }),
        name: isOnlyOneDevice ? 'newItem' : 'changedDevices',
        content: isOnlyOneDevice ? (
          <div>
            {currentValue && (
              <p
                style={{
                  marginTop: -16,
                  marginBottom: 4,
                  textAlign: 'right',
                  opacity: 0.6,
                }}
              >
                {currentValue}
                <SyncOutlined
                  style={{
                    marginLeft: 8,
                  }}
                />
              </p>
            )}
            <Input allowClear />
          </div>
        ) : (
          <EditableTable
            data={changedDevices}
            columns={columns}
            onChange={handleDevicesChange}
          />
        ),
      },
    ],
    [
      t,
      type,
      isOnlyOneDevice,
      currentValue,
      changedDevices,
      columns,
      handleDevicesChange,
    ],
  );

  const actionKey = useMemo(
    () =>
      type === 'ICCID'
        ? ActionTypes.PLASTIC_EXCHANGE
        : ActionTypes.MSISDN_EXCHANGE,
    [type],
  );

  const payloadToSend = useMemo(() => {
    const payload: { action: number; devices: { [key: string]: any }[] } = {
      action: actionKey,
      devices,
    };

    if (!isUpload) {
      const devicesData: { [key: string]: any }[] = isOnlyOneDevice
        ? treatedDevices
        : changedDevices;

      payload.devices = devicesData?.map(device => ({
        msisdn: device.msisdn,
        iccid: device.iccid,
        processData: isOnlyOneDevice ? newItem : device.processData,
      }));
    }

    return payload;
  }, [
    actionKey,
    changedDevices,
    devices,
    isOnlyOneDevice,
    isUpload,
    newItem,
    treatedDevices,
  ]);

  return {
    steps,
    payloadToSend,
  };
};

export default useExchangeStep;
